import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import {
  useQuery,
  useLazyQuery,
  useMutation,
  TRACKING_SEPARATOR,
} from '/@/core/graphql'
import { gql } from '@apollo/client/core'
import { type Product } from '/@/common/things/types'
import { productFragment } from './fragments'
import { type ProductInput } from './types'
import { evictFromCache } from '/@/utils/apollo'
import { useUser } from '/@/data/user'

export function useProductData(
  productId: MaybeRefOrGetter<string>,
  trackingComponent?: string,
) {
  const trackingComponentParam = trackingComponent
    ? `${TRACKING_SEPARATOR}${trackingComponent.replaceAll(' ', '')}`
    : ''

  const { result, loading, error } = useQuery<{ product: Product }>(
    gql`
      ${productFragment}
      query getProductDetail${trackingComponentParam}($id: String!) {
        product(id: $id) {
          ...ProductFields
        }
      }
    `,
    {
      id: toValue(productId),
    },
    () => ({
      enabled: Boolean(toValue(productId)),
    }),
  )

  const product = computed(() => result.value?.product)

  return { product, loading, error }
}

export function useProductDataLazy() {
  const { result, loading, error, load } = useLazyQuery<{ product: Product }>(
    gql`
      ${productFragment}
      query getProductDetailLazily($id: String!) {
        product(id: $id) {
          ...ProductFields
        }
      }
    `,
  )

  const product = computed(() => result.value?.product)
  return { product, loading, error, load }
}

export function useProductApi() {
  const user = useUser()

  const { mutate: create } = useMutation<{
    product: {
      create: { status: number; record?: Product; message?: { text: string } }
    }
  }>(gql`
    ${productFragment}
    mutation createProduct($input: ProductInput!) {
      product {
        create(input: $input) {
          record {
            ...ProductFields
            categoryKey
          }
          status
          message {
            text
          }
        }
      }
    }
  `)
  const { mutate: update } = useMutation<{
    product: {
      update: { status: number; record?: Product; message?: { text: string } }
    }
  }>(gql`
    ${productFragment}
    mutation updateProduct($id: String!, $input: ProductInput!) {
      product {
        update(id: $id, input: $input) {
          record {
            ...ProductFields
          }
          status
          message {
            text
          }
        }
      }
    }
  `)

  return {
    async create(input: ProductInput) {
      const response = await create(
        { input },
        {
          update: cache => {
            if (user.current?.designerId) {
              evictFromCache(
                cache,
                user.current.designerId,
                'Designer',
                'importDraftList',
              )
            }
          },
        },
      )
      return response?.data?.product?.create
    },
    async update(id: string, input: ProductInput) {
      const response = await update({ id, input })
      return response?.data?.product?.update
    },
  }
}
